import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { KpiCard, Panel } from "@/lovable-demo/components/bits";
import { LevelPill } from "@/lovable-demo/components/escalations";
import { supabase } from "@/lovable-demo/runtime/client";
import {
  dueLabel,
  isOpen,
  isOverdue,
  STATUS_LABEL,
  useEscalations,
  type Escalation,
} from "@/lovable-demo/lib/governance/escalations";
import { fmtCompactCurrency, recordDelayCost, recordNpr } from "@/lovable-demo/lib/warranty/calc";
import { classifySystem } from "@/lovable-demo/lib/warranty/safety";
import type { ProblemRecord } from "@/lovable-demo/lib/warranty/types";

/**
 * Financial lens over the governed escalations. Reads the same escalation rows
 * as the Problem Record, Executive Summary and Briefing — no copy, no writes.
 * Figures are decision intelligence only; they never set or rank the decision.
 */

interface Row {
  e: Escalation;
  r: ProblemRecord;
  actual: number;
  projected: number;
  containment: number | null;
  delayPerMonth: number;
  avoidance: number;
  recovery: number;
  safety: string | null;
}

function safetyFlag(e: Escalation, r: ProblemRecord): string | null {
  const sys = classifySystem(r);
  if (e.safety_regulatory || /safety|regulat|compliance/i.test(e.trigger_key))
    return sys.fmvss !== "—" ? `Safety · ${sys.fmvss}` : "Safety / regulatory trigger";
  return sys.fmvss !== "—" ? `Regulated system · ${sys.fmvss}` : null;
}

function useContainmentCost(ids: string[]) {
  const [cost, setCost] = useState<Record<string, number>>({});
  const key = ids.join(",");
  useEffect(() => {
    if (!ids.length) return;
    void supabase
      .from("record_containment_actions")
      .select("record_id, labor_cost, parts_cost, scrap_cost")
      .in("record_id", ids)
      .then(({ data }) => {
        const m: Record<string, number> = {};
        for (const c of data ?? [])
          m[c.record_id] =
            (m[c.record_id] ?? 0) +
            Number(c.labor_cost) +
            Number(c.parts_cost) +
            Number(c.scrap_cost);
        setCost(m);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return cost;
}

const Tag = ({ kind }: { kind: "Actual" | "Projected" | "Estimated" }) => (
  <span className="ml-1 rounded border border-border px-1 text-[0.6rem] uppercase tracking-wider text-muted-foreground">
    {kind}
  </span>
);

export function EscalatedFinancialExposure({ records }: { records: ProblemRecord[] }) {
  const { escalations, loading } = useEscalations();
  const byId = new Map(records.map((r) => [r.id, r]));
  const open = escalations.filter((e) => isOpen(e) && byId.has(e.record_id));
  const cost = useContainmentCost(open.map((e) => e.record_id));

  const rows: Row[] = open.map((e) => {
    const r = byId.get(e.record_id)!;
    const f = r.financial;
    const projected = recordNpr(r);
    return {
      e,
      r,
      actual: f.actualCost,
      projected,
      containment: cost[r.id] ?? null,
      delayPerMonth: recordDelayCost(r),
      avoidance: projected * (f.targetReductionPct / 100),
      recovery: Math.max(f.supplierRecoveryTarget - f.supplierRecovered, 0),
      safety: safetyFlag(e, r),
    };
  });
  // Ordered by due date, never by money — financial size does not set priority.
  rows.sort((a, b) => (a.e.due_at ?? "9").localeCompare(b.e.due_at ?? "9"));

  const sum = (fn: (x: Row) => number) => rows.reduce((a, x) => a + fn(x), 0);
  const awaiting = rows.filter((x) => x.e.status === "awaiting_decision");
  const safetyCount = rows.filter((x) => x.safety).length;

  return (
    <Panel
      title="Escalated Financial Exposure"
      subtitle="What is financially at stake in the decisions requiring management attention — from the same governed escalations shown on each Problem Record"
    >
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              label="Open escalation exposure"
              value={fmtCompactCurrency(sum((x) => x.r.financial.exposure))}
              tone="bad"
              hint={`${rows.length} open escalations · actual + projected`}
            />
            <KpiCard
              label="Exposure awaiting decision"
              value={fmtCompactCurrency(awaiting.reduce((a, x) => a + x.r.financial.exposure, 0))}
              tone="warn"
              hint={`${awaiting.length} awaiting a decision`}
            />
            <KpiCard
              label="Current cost at risk"
              value={fmtCompactCurrency(sum((x) => x.actual))}
              hint="Actual · booked to date"
            />
            <KpiCard
              label="Projected warranty / field exposure"
              value={fmtCompactCurrency(sum((x) => x.projected))}
              hint="Projected · remaining exposure"
            />
          </div>
          <details>
            <summary className="cursor-pointer text-xs font-semibold text-primary">
              More measures — containment, recovery, avoidance
            </summary>
            <div className="mt-2 grid gap-3 sm:grid-cols-3">
              <KpiCard
                label="Containment / action cost"
                value={fmtCompactCurrency(sum((x) => x.containment ?? 0))}
                hint="Actual · governed containment rows"
              />
              <KpiCard
                label="Supplier recovery opportunity"
                value={fmtCompactCurrency(sum((x) => x.recovery))}
                hint="Estimated · claimed minus recovered"
              />
              <KpiCard
                label="Potential cost avoidance"
                value={fmtCompactCurrency(sum((x) => x.avoidance))}
                tone="good"
                hint="Estimated · remaining exposure × target reduction"
              />
            </div>
          </details>

          {safetyCount > 0 && (
            <p className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-foreground">
              <ShieldAlert className="size-4 text-destructive" />
              {safetyCount} escalation{safetyCount > 1 ? "s" : ""} carry a safety / regulatory flag.
              That flag stands on its own and is never reduced or outweighed by financial figures.
            </p>
          )}

          <ul className="divide-y divide-border/60">
            {rows.length === 0 && (
              <li className="py-3 text-sm text-muted-foreground">
                No open escalations in this view.
              </li>
            )}
            {rows.map((x) => (
              <li key={x.e.id} className="py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Link
                    to="/demo/problem-record/$id"
                    params={{ id: x.r.id }}
                    hash="decision-queue"
                    className="hover:text-primary"
                  >
                    <span className="num text-xs font-semibold text-primary">
                      {x.e.record_code}
                    </span>{" "}
                    <span className="text-sm">{x.r.title}</span>
                  </Link>
                  <div className="flex items-center gap-2 text-xs">
                    <LevelPill e={x.e} />
                    {x.safety && (
                      <span className="inline-flex items-center gap-1 rounded border border-destructive/50 px-1.5 py-0.5 text-destructive">
                        <ShieldAlert className="size-3" /> {x.safety}
                      </span>
                    )}
                    <span className={isOverdue(x.e) ? "text-destructive" : "text-muted-foreground"}>
                      {STATUS_LABEL[x.e.status] ?? x.e.status} · {dueLabel(x.e)}
                    </span>
                  </div>
                </div>
                <p className="mt-1 text-xs text-foreground/85">
                  <span className="text-muted-foreground">Decision required:</span>{" "}
                  {x.e.decision_required || x.e.reason}
                  <span className="text-muted-foreground"> · Authority: </span>
                  {x.e.escalated_to_label}
                </p>
                <div className="num mt-2 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-3 xl:grid-cols-6">
                  <span>
                    Current exposure {fmtCompactCurrency(x.actual)}
                    <Tag kind="Actual" />
                  </span>
                  <span>
                    Projected {fmtCompactCurrency(x.projected)}
                    <Tag kind="Projected" />
                  </span>
                  <span>
                    Containment {x.containment == null ? "—" : fmtCompactCurrency(x.containment)}
                    <Tag kind="Actual" />
                  </span>
                  <span className="text-warning">
                    If delayed +{fmtCompactCurrency(x.delayPerMonth)}/mo
                    <Tag kind="Estimated" />
                  </span>
                  <span className="text-success">
                    Avoidance {fmtCompactCurrency(x.avoidance)}
                    <Tag kind="Estimated" />
                  </span>
                  <span>
                    Supplier recovery {x.recovery ? fmtCompactCurrency(x.recovery) : "—"}
                    <Tag kind="Estimated" />
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <p className="text-[0.7rem] text-muted-foreground">
            Financial figures inform the decision; they do not make it. Decisions are recorded only
            by the named authority on the Problem Record. Listed by due date, not by amount.
          </p>
        </div>
      )}
    </Panel>
  );
}
